import React, { useRef } from 'react';
import NavItem from './NavItem.jsx';
import NavIcon from './NavIcon.jsx';

/**
 * A collapsible nav group with animated children.
 *
 * In the expanded sidebar, clicking the parent toggles `is-open` (React state).
 * In the collapsed rail, the existing shared CSS shows the children as a
 * hover flyout keyed off `data-flyout-title`.
 *
 * @param {object} props
 * @param {object} props.group — `{ id, label, icon, defaultOpen?, children: [] }`
 * @param {string} props.currentRoute
 * @param {boolean} props.open
 * @param {Function} props.onToggleOpen — (groupId) => void
 * @param {Function} [props.onNavigate]
 */
export default function NavGroup({ group, currentRoute, open, onToggleOpen, onNavigate, collapsed = false }) {
    const active = group.children?.some((c) => c.route === currentRoute);
    const groupId = `navGroup-${group.id}`;
    const groupRef = useRef(null);

    const handleToggle = () => {
        const willOpen = !open;
        onToggleOpen(group.id);

        if (willOpen && !collapsed) {
            window.setTimeout(() => {
                groupRef.current?.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest',
                    behavior: 'smooth',
                });
            }, 240);
        }
    };

    return (
        <div ref={groupRef} className={`crm-nav-group${open ? ' is-open' : ''}${active ? ' active' : ''}`}>
            <button
                type="button"
                className="crm-nav-item"
                aria-expanded={open}
                aria-controls={groupId}
                data-nav-route-group={group.id}
                onClick={handleToggle}
            >
                <NavIcon name={group.icon} />
                <span className="crm-nav-label">{group.label}</span>
                <svg
                    className="crm-nav-chevron"
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                    aria-hidden="true"
                >
                    <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            <div
                className="crm-nav-children"
                id={groupId}
                data-flyout-title={group.label}
                role="group"
                aria-label={group.label}
            >
                {group.children.map((child) => (
                    <NavItem
                        key={child.route}
                        item={child}
                        currentRoute={currentRoute}
                        variant="sub"
                        collapsed={collapsed}
                        onNavigate={onNavigate}
                    />
                ))}
            </div>
        </div>
    );
}
